import { type BillingCodeStat } from '../../services/api/firmsService';
import { SimplifiedBillingCode } from '../../services/types/client-intake-types';

/**
 * Configuration for billing code selection UI
 *
 * Each group represents a category of billing types with related options.
 * Groups are rendered as visually separated sections.
 */

export interface BillingOption {
  code: SimplifiedBillingCode;
  label: string;
  description: string;
  note?: string;
}

export interface BillingGroup {
  title: string;
  options: BillingOption[];
}

export const BILLING_GROUPS: BillingGroup[] = [
  {
    title: 'Limited Scope',
    options: [
      {
        code: SimplifiedBillingCode.CASE_SETUP_ONLY,
        label: 'Case Setup Only',
        description: 'Opening claims, sending LORs & collecting police reports',
      },
      {
        code: SimplifiedBillingCode.DEMAND_ONLY,
        label: 'Demand Only',
        description: 'Demand letter drafting only',
      },
      {
        code: SimplifiedBillingCode.LITIGATION_ONLY,
        label: 'Litigation Only',
        description: 'Post-suit litigation support',
      },
      {
        code: SimplifiedBillingCode.PROPERTY_DAMAGE,
        label: 'Property Damage',
        description: 'Property damage claims only',
      },
      {
        code: SimplifiedBillingCode.RECORDS_RETRIEVAL,
        label: 'Records Retrieval',
        description: 'Ordering and collecting medical records',
      },
    ],
  },
  {
    title: 'Transfer',
    options: [
      {
        code: SimplifiedBillingCode.IN_FLIGHT,
        label: 'In Progress Case',
        description: 'Picking up an active case mid-stream (liability + insurance already addressed)',
      },
    ],
  },
  {
    title: 'Full Service',
    options: [
      {
        code: SimplifiedBillingCode.BACK_OFFICE,
        label: 'Back Office',
        description: 'Pre-lit support without client communication',
      },
      {
        code: SimplifiedBillingCode.FULL_PRE_LIT,
        label: 'Full Pre Lit',
        description: 'Case setup through demand letter',
      },
      {
        code: SimplifiedBillingCode.FULL_PRE_LIT_PROPERTY_DAMAGE,
        label: 'Full Pre Lit and Property Damage',
        description: 'Case setup through demand, plus property damage',
      },
    ],
  },
];

/** All billing options in a flat list (no group headers), alphabetical. */
export const BILLING_OPTIONS_FLAT: BillingOption[] = BILLING_GROUPS.flatMap(
  (group) => group.options,
).sort((a, b) => a.label.localeCompare(b.label));

/**
 * Build a usage-note string from billing code stats.
 * Returns e.g. "7 of 13 matters" for codes with usage,
 * or undefined when the code has no usage data.
 */
function usageNote(
  code: SimplifiedBillingCode,
  stats: BillingCodeStat[],
): string | undefined {
  const stat = stats.find((s) => s.code === code);
  if (!stat || stat.count === 0 || stat.total === 0) return undefined;
  return `${stat.count} of ${stat.total} matters`;
}

/**
 * Return BILLING_GROUPS with options sorted by firm usage (most-used first)
 * and annotated with usage notes. Groups keep their structure but options
 * within each group are reordered by frequency.
 */
export function getBillingGroupsForFirm(
  stats: BillingCodeStat[] | undefined,
): BillingGroup[] {
  if (!stats || stats.length === 0) return BILLING_GROUPS;

  // Build a rank map: code -> index in stats (already sorted by count desc)
  const rank = new Map(stats.map((s, i) => [s.code, i]));

  return BILLING_GROUPS.map((group) => ({
    ...group,
    options: [...group.options]
      .sort((a, b) => {
        const aRank = rank.get(a.code) ?? Number.MAX_SAFE_INTEGER;
        const bRank = rank.get(b.code) ?? Number.MAX_SAFE_INTEGER;
        return aRank - bRank;
      })
      .map((opt) => ({
        ...opt,
        note: usageNote(opt.code, stats) ?? opt.note,
      })),
  }));
}

/**
 * Return a flat list of billing options sorted by firm usage (most-used first)
 * and annotated with usage notes.
 */
export function getBillingOptionsFlatForFirm(
  stats: BillingCodeStat[] | undefined,
): BillingOption[] {
  if (!stats || stats.length === 0) return BILLING_OPTIONS_FLAT;

  const rank = new Map(stats.map((s, i) => [s.code, i]));

  return [...BILLING_OPTIONS_FLAT]
    .sort((a, b) => {
      const aRank = rank.get(a.code) ?? Number.MAX_SAFE_INTEGER;
      const bRank = rank.get(b.code) ?? Number.MAX_SAFE_INTEGER;
      return aRank - bRank;
    })
    .map((opt) => ({
      ...opt,
      note: usageNote(opt.code, stats) ?? opt.note,
    }));
}
